import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";

const CELL_SIZE = 10;
const UPDATE_INTERVAL = 100; // ms

const Background: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const cols = Math.floor(canvas.width / CELL_SIZE);
    const rows = Math.floor(canvas.height / CELL_SIZE);

    let grid = createGrid(rows, cols);

    const draw = () => {
      if (!ctx) return;

      ctx.fillStyle =
        theme.palette.mode === "dark"
          ? theme.palette.background.default
          : "#fafafa";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          if (grid[y][x]) {
            ctx.fillStyle =
              // theme.palette.mode === "dark" ? "#90caf9" : "#1976d2";
              theme.palette.mode === "dark" ? "#ffffff33" : "#00000033";
            ctx.fillRect(
              x * CELL_SIZE,
              y * CELL_SIZE,
              CELL_SIZE - 1,
              CELL_SIZE - 1
            );
          }
        }
      }
    };

    const update = () => {
      grid = nextGen(grid);
      draw();
    };

    const interval = setInterval(update, UPDATE_INTERVAL);
    draw();

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", resize);
    };
  }, [theme.palette.background.default, theme.palette.mode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: -1,
        width: "100vw",
        height: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    />
  );
};

// --- Helper functions ---

function createGrid(rows: number, cols: number): number[][] {
  const grid = Array.from({ length: rows }, () => Array(cols).fill(0));

  // horizontaler Mittelbereich
  const startCol = Math.floor(cols / 2) - 4;
  const endCol = Math.floor(cols / 2) + 4;

  // // vertikaler Mittelbereich
  // const startRow = Math.floor(rows / 2) - 4;
  // const endRow = Math.floor(rows / 2) + 4;

  // vertikaler Boden
  const startRow = rows-9;
  const endRow = rows-1;

  for (let y = startRow; y <= endRow; y++) {
    for (let x = startCol; x <= endCol; x++) {
      grid[y][x] = Math.random() > 0.5 ? 1 : 0;
    }
  }

  // Gosper Glider Gun platzieren (links oben bei [1][1])
  // Quelle: https://conwaylife.com/wiki/Gosper_glider_gun
  const gunPattern: [number, number][] = [
    [5, 1], [5, 2], [6, 1], [6, 2],
    [5, 11], [6, 11], [7, 11],
    [4, 12], [8, 12],
    [3, 13], [9, 13],
    [3, 14], [9, 14],
    [6, 15],
    [4, 16], [8, 16],
    [5, 17], [6, 17], [7, 17],
    [6, 18],
    [3, 21], [4, 21], [5, 21],
    [3, 22], [4, 22], [5, 22],
    [2, 23], [6, 23],
    [1, 25], [2, 25], [6, 25], [7, 25],
    [3, 35], [4, 35], [3, 36], [4, 36]
  ];

  let startX = 1;
  let startY = 15;

  for (const [y, x] of gunPattern) {
    const newY = startY + y;
    const newX = startX + x;

    // Prüfen, dass wir nicht außerhalb des Grids schreiben
    if (newY >= 0 && newY < grid.length && newX >= 0 && newX < grid[0].length) {
      grid[newY][newX] = 1;
    }
  }

  return grid;
}


function nextGen(grid: number[][]): number[][] {
  const rows = grid.length;
  const cols = grid[0].length;
  const newGrid = grid.map(arr => [...arr]);

  const getNeighbors = (x: number, y: number) => {
    let sum = 0;
    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        if (i === 0 && j === 0) continue;
        const nx = x + j;
        const ny = y + i;
        if (nx >= 0 && nx < cols && ny >= 0 && ny < rows) {
          sum += grid[ny][nx];
        }
      }
    }
    return sum;
  };

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const neighbors = getNeighbors(x, y);
      const cell = grid[y][x];
      if (cell === 1 && (neighbors < 2 || neighbors > 3)) newGrid[y][x] = 0;
      else if (cell === 0 && neighbors === 3) newGrid[y][x] = 1;
    }
  }
  return newGrid;
}

export default Background;
